import clsx from 'clsx';

import { TextField } from './TextField';

interface NumberFieldProps {
  value: number | '';
  onChange?: (value: number | '') => void;
  onBlur?: () => void;
  min?: number;
  max?: number;
  label?: string;
  errorMessage?: string;
  id?: string;
  placeholder?: string;
  className?: string;
  isWidthFull?: boolean;
  updateFn?: (fieldName: string, value: number | '') => void;
  validateFn?: () => void;
}

export function NumberField({
  value,
  onChange,
  onBlur,
  min = 1,
  max = 10,
  label,
  errorMessage,
  id,
  placeholder,
  className,
  isWidthFull = false,
  updateFn,
  validateFn,
}: NumberFieldProps) {
  const handleValueChange = (newValue: number | '') => {
    if (updateFn && id) {
      updateFn(id, newValue);
    } else if (onChange) {
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    if (validateFn) {
      validateFn();
    } else if (onBlur) {
      onBlur();
    }
  };

  const handleButtonClick = (newValue: number | '') => {
    handleValueChange(newValue);
    // Trigger validation after updating the value
    if (validateFn) {
      validateFn();
    } else if (onBlur) {
      onBlur();
    }
  };

  return (
    <div
      className={clsx('relative flex flex-col', {
        'col-span-full': isWidthFull,
      })}
    >
      <TextField
        className={className}
        errorMessage={errorMessage}
        id={id}
        label={label}
        max={max}
        min={min}
        placeholder={placeholder}
        updateFn={updateFn}
        validateFn={validateFn}
        value={value}
        onBlur={handleBlur}
        onChange={(e) => {
          const inputValue = e.target.value;
          if (inputValue === '') {
            handleValueChange('');
            return;
          }

          const parsed = parseInt(inputValue, 10);
          if (!isNaN(parsed)) {
            const clamped = Math.min(Math.max(parsed, min), max);
            handleValueChange(clamped);
          }
        }}
      />
      <div className="absolute right-0 top-10 flex h-[42px] flex-row rounded-md text-lg font-semibold sm:h-[46px]">
        <button
          aria-label="Decrement value"
          className="flex h-full w-10 items-center justify-center rounded-l border-t border-b border-l border-gray-300 bg-blue-50 hover:bg-blue-200"
          type="button"
          onClick={() => {
            const newValue = Math.max((value === '' ? min : value) - 1, min);
            handleButtonClick(newValue);
          }}
        >
          -
        </button>
        <button
          aria-label="Increment value"
          className="flex h-full w-10 items-center justify-center rounded-r border-t border-b border-r border-l border-gray-300 hover:bg-blue-200"
          type="button"
          onClick={() => {
            const newValue = Math.min((value === '' ? min : value) + 1, max);
            handleButtonClick(newValue);
          }}
        >
          +
        </button>
      </div>
    </div>
  );
}
